import {
  CompanyBadgesTableRow,
  CustomerTypeFilterOption,
  FilterProps, PaymentStatusFilterOption,
  ProductFilterOption,
  StatusFilterOption
} from "../../../types";
import { useEffect, useState } from "react";
import { IPersonBadge, IProduct } from "../../../../../common/types";
import { BadgeStatuses } from "../../../../../common/enums";
import { useTranslation } from "react-i18next";
import BadgeHelper from "../../../../../common/network/helpers/badges/BadgeHelper";
import camelCase from 'lodash.camelcase';

type Props = {
  updateTableData: (tableData: CompanyBadgesTableRow[]) => void;
  tableData: CompanyBadgesTableRow[];
  products: IProduct[];
}

const FILTERS_ALL_TYPE = 'all';

export const useCompanyBadgesFilters = ({ tableData, updateTableData, products }: Props) => {
  const [selectedBadgeStatus, setSelectedBadgeStatus] = useState(FILTERS_ALL_TYPE);
  const [selectedCustomerType, setSelectedCustomerType] = useState(FILTERS_ALL_TYPE);
  const [selectedBadgePaymentStatus, setSelectedBadgePaymentStatus] = useState(FILTERS_ALL_TYPE);
  const [selectedProduct, setSelectedProduct] = useState(FILTERS_ALL_TYPE);

  const { t } = useTranslation('common');
  const [statusOptions, setStatusOptions] = useState<StatusFilterOption[]>([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState<PaymentStatusFilterOption[]>([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState<CustomerTypeFilterOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductFilterOption[]>([]);

  const onBadgeStatusFilterChange = (status: string) => {
    updateTableData(tableData);
    setSelectedBadgeStatus(status);
  }

  const deselectFilters = () => {
    setSelectedProduct(FILTERS_ALL_TYPE);
    setSelectedBadgeStatus(FILTERS_ALL_TYPE);
    setSelectedCustomerType(FILTERS_ALL_TYPE);
    setSelectedBadgePaymentStatus(FILTERS_ALL_TYPE);
  }

  const onBadgeProductChange = (product: string) => {
    updateTableData(tableData);
    setSelectedProduct(product);
  }

  useEffect(() => {
    filterTableData(tableData);
  }, [selectedCustomerType, selectedBadgeStatus, selectedProduct, selectedBadgePaymentStatus]);

  const filterTableData = (tableData: CompanyBadgesTableRow[]) => {
    const filtered = tableData.filter((data) => {
      const badge = data[Object.keys(data).length - 1] as IPersonBadge;
      let isBadgeStatusValid = true;
      let isBadgeCustomerTypeValid = true;
      let isProductValid = true;
      let isBadgePaymentStatusValid = true;

      if (selectedBadgeStatus !== FILTERS_ALL_TYPE) {
        isBadgeStatusValid = badge.status === selectedBadgeStatus;
      }
      if (selectedCustomerType !== FILTERS_ALL_TYPE) {
        isBadgeCustomerTypeValid = isBadgeStatusValid && badge.customerType === selectedCustomerType;
      }
      if (selectedProduct !== FILTERS_ALL_TYPE) {
        isProductValid = isBadgeStatusValid && isBadgeCustomerTypeValid && badge.productCode === selectedProduct;
      }
      if (selectedBadgePaymentStatus !== FILTERS_ALL_TYPE) {
        isBadgePaymentStatusValid = isProductValid && badge.paymentStatus === selectedBadgePaymentStatus;
      }
      return isBadgeStatusValid && isBadgeCustomerTypeValid && isProductValid && isBadgePaymentStatusValid;
    });
    updateTableData(filtered);
  }

  const getLabelByStatus = (status: BadgeStatuses) => {
    return t(`badgeStatuses.${status.toLowerCase()}`)
  }

  const configureStatusOptions = (tableDataForStatus: Props['tableData']) => {
    const configuredOptions: StatusFilterOption[] = [{ label: t('companyPage.all'), status: FILTERS_ALL_TYPE }];
    tableDataForStatus.forEach((data) => {
      const badge = data[Object.keys(data).length - 1] as IPersonBadge;

      const isAlreadySelected = configuredOptions.find(({ status }) => status === badge.status);
      if (!isAlreadySelected) {
        configuredOptions.push({ label: getLabelByStatus(badge.status), status: badge.status });
      }
    })
    setStatusOptions(configuredOptions);
  }

  const configurePaymentStatusOptions = (tableDataForStatus: Props['tableData']) => {
    const configuredOptions: PaymentStatusFilterOption[] = [{ label: t('companyPage.all'), status: FILTERS_ALL_TYPE }];
    tableDataForStatus.forEach((data) => {
      const badge = data[Object.keys(data).length - 1] as IPersonBadge;

      const isAlreadySelected = configuredOptions.find(({ status }) => status === badge.paymentStatus);
      if (!isAlreadySelected && BadgeHelper.isBadgePaymentStatusValid(badge.paymentStatus)) {
        configuredOptions.push({ label: t(`paymentStatuses.${camelCase(badge.paymentStatus!)}`), status: badge.paymentStatus! });
      }
    })
    setPaymentStatusOptions(configuredOptions);
  }


  const configureProductOptions = (tableDataForProducts: Props['tableData']) => {
    const configuredOptions: ProductFilterOption[] = [{ label: t('companyPage.all'), sku: FILTERS_ALL_TYPE }];
    tableDataForProducts.forEach((data) => {
      const badge = data[Object.keys(data).length - 1] as IPersonBadge;

      const isAlreadySelected = configuredOptions.find(({ sku }) => sku === badge.productCode);
      if (!isAlreadySelected) {
        const product = products.find(({sku}) => sku === badge.productCode);
        if (product) {
          configuredOptions.push({ label: product.name, sku: product.sku });
        }
      }
    });
    setProductOptions(configuredOptions);
  }

  const configureCustomerTypeOptions = (tabledDataForCustomerType: Props['tableData']) => {
    const configuredOptions: CustomerTypeFilterOption[] = [{ type: FILTERS_ALL_TYPE, label: t('companyPage.all') }];

    tabledDataForCustomerType.forEach((data) => {
      const badge = data[Object.keys(data).length - 1] as IPersonBadge;

      const isAlreadySelected = configuredOptions.find(({ type }) => type === badge.customerType);
      if (!isAlreadySelected) {
        configuredOptions.push({ label: t(`customerTypesTitles.${badge.customerType}`), type: badge.customerType });
      }
    });
    setCustomerTypeOptions(configuredOptions);
  }

  const configureOptionsForFilters = (tableDataToConfigure: Props['tableData']) => {
    configureStatusOptions(tableDataToConfigure);
    configureCustomerTypeOptions(tableDataToConfigure);
    configureProductOptions(tableDataToConfigure);
    configurePaymentStatusOptions(tableDataToConfigure);
  }

  const onCustomerTypeFilterChange = (customerType: string) => {
    updateTableData(tableData);
    setSelectedCustomerType(customerType);
  }

  const onBadgePaymentStatusFilterChange = (status: string) => {
    updateTableData(tableData);
    setSelectedBadgePaymentStatus(status);
  }

  return {
    config: {
      configureOptionsForFilters,
    },
    filterGetters: {
      selectedBadgeStatus,
      selectedCustomerType,
      statusOptions,
      customerTypeOptions,
      paymentStatusOptions,
      selectedProduct,
      productOptions,
      selectedBadgePaymentStatus
    },
    filterSetters: {
      onBadgeStatusFilterChange,
      onCustomerTypeFilterChange,
      onBadgePaymentStatusFilterChange,
      onBadgeProductChange,
      deselectFilters
    },
    filterTableData
  } as FilterProps
}
